import React, { useMemo } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    Decimation,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    Decimation
);

type Props = {
    labels: (string | number | Date)[];
    values: Array<number | null>;      // поддержка разрывов (null => gap)
    height?: number;                   // px, по умолчанию 220
    currency?: string;                 // 'CAD' | 'USD' | ...
    showLegend?: boolean;              // по умолчанию скрыт (один датасет)
    color?: string;                    // цвет линии, по умолчанию #3B82F6 (tailwind blue-500)
    label?: string;                    // подпись датасета
};

export default function Chart({
    labels,
    values,
    height = 220,
    currency = "CAD",
    showLegend = false,
    color = "rgb(59, 130, 246)",
    label = "Average price",
}: Props) {
    const numberFmt = useMemo(
        () =>
            new Intl.NumberFormat(undefined, {
                style: "currency",
                currency,
                maximumFractionDigits: 2,
            }),
        [currency]
    );

    const data = useMemo(() => {
        return {
            labels,
            datasets: [
                {
                    label,
                    data: values,
                    borderColor: color,
                    borderWidth: 2,
                    fill: true,
                    backgroundColor: (ctx: any) => {
                        const { chart } = ctx;
                        const { ctx: canvas, chartArea } = chart;
                        if (!chartArea) return "rgba(59,130,246,0.15)"; // первый рендер
                        const g = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                        g.addColorStop(0, "rgba(59,130,246,0.25)");
                        g.addColorStop(1, "rgba(59,130,246,0.02)");
                        return g;
                    },
                    tension: 0.35,        // сглаживание
                    pointRadius: 0,       // чистая линия
                    pointHoverRadius: 4,
                    hitRadius: 8,
                    spanGaps: true,       // пропускать null как разрывы
                },
            ],
        };
    }, [labels, values, color, label]);

    const options = useMemo(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index" as const, intersect: false },
            plugins: {
                legend: { display: showLegend },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        label: (ctx: any) => {
                            const v = ctx.parsed?.y;
                            if (v == null || Number.isNaN(v)) return "";
                            return `${ctx.dataset.label || "Price"}: ${numberFmt.format(v)}`;
                        },
                        title: (items: any[]) => String(items?.[0]?.label ?? ""),
                    },
                },
                decimation: { enabled: true, algorithm: "min-max" as const }, // производительность на длинных рядах
            },
            elements: {
                line: { borderCapStyle: "round" as const },
                point: { borderWidth: 0 },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { maxRotation: 0, autoSkipPadding: 16 },
                },
                y: {
                    beginAtZero: false,
                    ticks: {
                        callback: (val: any) => numberFmt.format(Number(val)),
                    },
                    grid: {
                        drawBorder: false,
                        color: (ctx: any) =>
                            ctx.index % 2 ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.03)",
                    },
                },
            },
        };
    }, [numberFmt, showLegend]);

    return (
        <div className="w-full" style={{ height }}>
            <Line data={data} options={options as any} />
        </div>
    );
}